import { SeoLandingLayout, SeoLandingPageData } from '../../components/SeoLandingLayout';

const data: SeoLandingPageData = {
  seo: {
    title: 'Prévision de trésorerie : anticiper avant que ça coince | Trezoone',
    description: 'Faites votre prévision de trésorerie et identifiez les mois à risque avant qu\'ils arrivent. Outil simple et concret pour TPE, PME et associations.',
    canonical: '/prevision-tresorerie',
    keywords: 'prévision trésorerie, prévision de trésorerie, anticiper trésorerie, projection trésorerie, cash flow prévisionnel',
  },
  hero: {
    tag: 'Prévision de trésorerie',
    title: 'Savoir dans 3 mois si votre compte sera dans le vert ou dans le rouge.',
    subtitle: 'La prévision de trésorerie, c\'est l\'art d\'anticiper les tensions avant qu\'elles deviennent des urgences. Pas besoin d\'être expert-comptable. Il faut juste les bons outils.',
    ctaLabel: 'Faire ma prévision',
    ctaHref: '/simulateur-tresorerie-association',
  },
  pilierLink: {
    label: 'Guide complet : gestion de trésorerie',
    href: '/gestion-tresorerie',
  },
  sections: [
    {
      type: 'hook',
      title: 'Gérer sa trésorerie dans le rétroviseur, ça ne suffit plus',
      content: 'Regarder ce qui s\'est passé sur votre compte bancaire, c\'est utile. Mais ça ne vous dit pas si vous tiendrez dans 3 mois. La prévision, c\'est regarder devant.',
      items: [
        'Sans prévision : vous subissez les tensions, vous réagissez trop tard',
        'Avec prévision : vous voyez les creux à l\'avance, vous pouvez agir (négocier, décaler, financer)',
        'La différence entre une structure qui survit et une qui coule, c\'est souvent 3 semaines d\'anticipation',
      ],
    },
    {
      type: 'explain',
      title: 'Comment fonctionne une prévision de trésorerie ?',
      content: 'Une prévision de trésorerie projette vos flux de cash futurs sur une période donnée. Elle part de votre solde actuel et ajoute mois par mois les encaissements prévus, puis soustrait les décaissements prévus.',
      items: [
        'Solde initial = votre trésorerie aujourd\'hui',
        'Encaissements prévisionnels = ventes attendues, subventions, loyers, remboursements',
        'Décaissements prévisionnels = salaires, charges, fournisseurs, emprunts, impôts',
        'Solde prévisionnel = solde initial + encaissements - décaissements (mois par mois)',
        'Les mois où le solde passe en négatif = vos zones de danger à traiter maintenant',
      ],
    },
    {
      type: 'example',
      title: 'Prévision trésorerie — PME de services : exemple chiffré',
      content: 'Une PME de 8 personnes, CA mensuel moyen 45 000 €, délai de paiement client 45 jours.',
      items: [
        'Janvier : CA facturé 50 000 € — encaissé 35 000 € (paiement de novembre) → tension sur les charges',
        'Février : Charges sociales trimestrielles + TVA → décaissement exceptionnel 28 000 €',
        'Mars : Les factures de janvier rentrent → 50 000 € encaissés → respiration',
        'Sans prévision : découvert de 12 000 € en février, appel en urgence à la banque',
        'Avec prévision : découvert autorisé négocié en décembre, zéro surprise',
      ],
    },
    {
      type: 'midcta',
      title: 'Voyez votre solde dans 3, 6 et 12 mois — dès aujourd\'hui',
      content: 'Essai gratuit 14 jours · Données bancaires importées en 5 min · Résultats immédiats',
    },
    {
      type: 'solution',
      title: 'Trezoone : votre outil de prévision de trésorerie',
      content: 'Trezoone connecte vos données réelles (relevés bancaires) à vos prévisions pour créer un tableau de bord de trésorerie précis et toujours à jour.',
      items: [
        'Prévision sur 12 mois intégrant vos données bancaires réelles',
        'Identification automatique des mois à risque (solde négatif prévu)',
        'Simulation de scénarios : retard de paiement, baisse d\'activité, hausse de charges',
        'Alertes par email quand votre solde prévu descend sous un seuil',
        'Rapport mensuel automatique pour suivre prévisionnel vs réel',
        'Accessible à votre comptable, votre CA ou vos financeurs',
      ],
    },
    {
      type: 'faq',
      title: 'Questions sur la prévision de trésorerie',
      faq: [
        {
          q: 'Sur quelle période faire une prévision de trésorerie ?',
          a: '3 à 6 mois pour les besoins opérationnels courants. 12 mois pour la planification et les demandes de financement. Trezoone propose une prévision sur 12 mois glissants.',
        },
        {
          q: 'Peut-on faire une prévision sans être expert financier ?',
          a: 'Oui. Une prévision de trésorerie ne demande pas de compétences comptables. Il faut juste lister ce qui va rentrer et ce qui va sortir, avec les bonnes dates. Trezoone guide l\'utilisateur étape par étape.',
        },
        {
          q: 'Quelle est la précision d\'une prévision de trésorerie ?',
          a: 'Plus vous avez de données historiques et de contrats récurrents, plus la prévision est précise. Pour les éléments incertains, construisez des scénarios (avec/sans la recette). La précision s\'améliore avec l\'usage.',
        },
        {
          q: 'Comment intégrer les impayés dans une prévision ?',
          a: 'Appliquez un taux de retard moyen à vos créances clients. Si 20 % de vos clients paient avec 30 jours de retard, décalez 20 % de vos encaissements d\'un mois dans votre prévision.',
        },
      ],
    },
    {
      type: 'cluster',
      title: 'Prévision et anticipation par profil',
      links: [
        { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
        { label: 'Logiciel de prévision trésorerie', href: '/logiciel-prevision-tresorerie' },
        { label: 'Outil prévision trésorerie', href: '/outil-prevision-tresorerie' },
        { label: 'Gestion trésorerie PME', href: '/gestion-tresorerie-pme' },
        { label: 'Simulation trésorerie association', href: '/simulation-tresorerie-association' },
        { label: 'Stress test trésorerie', href: '/stress-test-tresorerie' },
        { label: 'Manque de trésorerie', href: '/manque-tresorerie' },
        { label: 'Alerte trésorerie', href: '/alerte-tresorerie' },
      ],
    },
    {
      type: 'cta',
      title: 'Commencez votre prévision de trésorerie dès aujourd\'hui',
      content: 'Essai gratuit 14 jours. Importez vos données en 5 minutes. Voyez où vous en serez dans 3 mois.',
    },
  ],
  relatedLinks: [
    { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
    { label: 'Gestion de trésorerie', href: '/gestion-tresorerie' },
    { label: 'Logiciel de trésorerie', href: '/logiciel-tresorerie' },
    { label: 'Simulateur', href: '/simulateur-tresorerie-association' },
  ],
};

interface Props { onNavigate?: (p: string) => void; onSignIn?: () => void; onSignUp?: () => void; }

export default function PrevisionTresorerieSeoPage({ onNavigate, onSignIn, onSignUp }: Props) {
  return <SeoLandingLayout data={data} onNavigate={onNavigate} onSignIn={onSignIn} onSignUp={onSignUp} />;
}
